import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import Tag from '_components/UI/Tag';
import TextButton from '_components/UI/TextButton';
import { CartAddIcon } from '_components/QuickAdd/QuickAddTrigger';
import type { BundleStripImage } from '@adapters/prismic/transforms/bundleStrip';

import styles from './BundleCard.module.scss';

export interface BundleCardProps {
  badgeLabel: string | null;
  image: BundleStripImage | null;
  ctaLabel: string;
  total: string;
  originalTotal: string | null;
  savings: string | null;
  isFromPrice: boolean;
  isAdding: boolean;
  message: string | null;
  onAdd: () => void;
  onIntent: () => void;
  priority?: boolean;
}

const BundleCard = ({
  badgeLabel,
  image,
  ctaLabel,
  total,
  originalTotal,
  savings,
  isFromPrice,
  isAdding,
  message,
  onAdd,
  onIntent,
  priority = false,
}: BundleCardProps) => {
  const { t } = useTranslation('common');

  return (
    <div className={styles.card}>
      {badgeLabel && (
        <Tag color="green" size="small" className={styles.badge}>
          {badgeLabel}
        </Tag>
      )}
      {image && (
        <div className={styles.imageWrapper}>
          <Image
            src={image.url}
            alt={image.alt ?? ''}
            fill
            sizes="(min-width: 988px) 445px, 0px"
            className={styles.image}
            priority={priority}
          />
        </div>
      )}
      <div className={styles.body}>
        <p className={styles.title}>{t('bundleStrip.totalBundlePrice')}</p>
        <div className={styles.prices}>
          <p className={styles.priceRow}>
            <span className={styles.total}>
              {isFromPrice
                ? t('bundleStrip.fromPrice', { price: total })
                : total}
            </span>
            {originalTotal && (
              <span className={styles.originalTotal}>
                <span className={styles.srOnly}>
                  {t('bundleStrip.originalPrice', { price: originalTotal })}
                </span>
                <s aria-hidden>{originalTotal}</s>
              </span>
            )}
          </p>
          {savings && (
            <p className={styles.savings}>
              {t('bundleStrip.youSave', { amount: savings })}
            </p>
          )}
        </div>
        <TextButton
          type="primary"
          size="medium"
          className={styles.cta}
          label={isAdding ? t('bundleStrip.adding') : ctaLabel}
          leadingIcon={CartAddIcon}
          leadingIconSize={24}
          disabled={isAdding}
          onClick={onAdd}
          onMouseEnter={onIntent}
          onFocus={onIntent}
        />
        <p className={styles.message} role="status">
          {message}
        </p>
      </div>
    </div>
  );
};

export default BundleCard;
